import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { Navigate, useLocation } from 'react-router-dom';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import LoadingSpinner from './LoadingSpinner';
import {
  clearAuthSessionStorage,
  notifyAuthSessionChanged,
  isJwtExpired,
} from '../../utils/authUtils';

const rejectTrainerSession = (reason) => {
  clearAuthSessionStorage();
  notifyAuthSessionChanged(reason);
};

const TrainerProtectedRoute = ({ children }) => {
  const { isLoading } = useSimpleAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkTrainerAuth = async () => {
      try {
        // ตรวจสอบว่าไม่ใช่ admin (admin ไม่ควรเข้า trainer dashboard)
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
          console.log('❌ Admin cannot access trainer dashboard');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // ตรวจสอบ trainer token ใน localStorage
        const token = localStorage.getItem('authToken') || localStorage.getItem('fitconnect_token');
        const userData = localStorage.getItem('authUser') || localStorage.getItem('fitconnect_user');
        
        console.log('🔍 TrainerProtectedRoute checking credentials:', { 
          hasToken: !!token, 
          hasUserData: !!userData,
          pathname: location.pathname 
        });
        
        if (!token || !userData) {
          console.log('❌ No trainer credentials found');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // Parse user data เพื่อตรวจสอบ role
        let user;
        try {
          user = JSON.parse(userData);
        } catch (e) {
          console.log('❌ Invalid user data format');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // ตรวจสอบว่าเป็น trainer หรือไม่
        const userRole = (user.role || '').toLowerCase();
        const isTrainer = userRole === 'trainer' || userRole === 'เทรนเนอร์';

        if (!isTrainer) {
          console.log('❌ User is not a trainer:', userRole);
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        if (isJwtExpired(token)) {
          console.log('❌ Trainer token expired');
          rejectTrainerSession('trainer_token_expired');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }

        // ตรวจสอบกับ server (optional - สำหรับความปลอดภัยเพิ่มเติม)
        try {
          console.log('🔍 Verifying trainer token with server...');
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(`${API_CONFIG.BASE_URL || (process.env.NODE_ENV === 'production' 
        ? 'https://www.ptthaiplus.com/ptthaiplus_app/api'
        : API_CONFIG.BASE_URL)}/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (!response.ok) {
            if (response.status === 401) {
              console.log('❌ Trainer token invalid or expired (401)');
              rejectTrainerSession('trainer_verify_401');
              setChecking(false);
              setIsAuthorized(false);
              return;
            }
            throw new Error(`Token verification failed: ${response.status}`);
          }

          const data = await response.json();
          console.log('✅ Trainer authentication verified:', data);
          
          // หมายเหตุ: ไม่ใช้ role จาก server ในการบล็อกแล้ว
          // ใช้เฉพาะสำหรับยืนยันว่า token ยังใช้ได้อยู่
          // role หลักสำหรับตัดสินใจให้สิทธิ์ ใช้จาก userData ฝั่ง frontend ด้านบน
          setIsAuthorized(true);
          setChecking(false);
        } catch (error) {
          console.log('⚠️ Trainer token verification failed:', error.message);
          
          // ถ้าเป็น network error หรือ timeout ให้ใช้ client-side validation แทน
          if (error.message.includes('Failed to fetch') || 
              error.message.includes('NetworkError') || 
              error.name === 'AbortError') {
            console.log('🌐 Network error or timeout, using client-side validation');
            setIsAuthorized(true);
            setChecking(false);
            return;
          }
          
          rejectTrainerSession('trainer_verify_failed');
          setChecking(false);
          setIsAuthorized(false);
          return;
        }
      } catch (error) {
        console.error('Error checking trainer auth:', error);
        setChecking(false);
        setIsAuthorized(false);
      }
    };

    checkTrainerAuth();
  }, [location.pathname]);

  // แสดง loading ขณะตรวจสอบ
  if (isLoading || checking) {
    return <LoadingSpinner fullScreen text="กำลังตรวจสอบสิทธิ์เทรนเนอร์..." />;
  }

  // ถ้าไม่มีสิทธิ์ ให้ redirect ไปหน้า login
  if (!isAuthorized) {
    // ตรวจสอบว่ามี admin token หรือไม่ เพื่อแสดง message ที่เหมาะสม
    const adminToken = localStorage.getItem('adminToken');
    const message = adminToken 
      ? 'แอดมินไม่สามารถเข้าถึงหน้าเทรนเนอร์ได้'
      : 'กรุณาเข้าสู่ระบบด้วยบัญชีเทรนเนอร์';

    console.log('🚫 Not authorized - redirecting to signin');
    return (
      <Navigate 
        to="/signin" 
        state={{ 
          from: location.pathname,
          message: message
        }} 
        replace 
      />
    );
  }

  // ถ้าผ่านการตรวจสอบแล้ว ให้แสดงหน้าเทรนเนอร์
  return children;
};

export default TrainerProtectedRoute;

